import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { defineMessage, MessageDescriptor } from "react-intl"
import { DbgPaths, Paths, TtcPaths } from "../../../../utils/Paths"
import { ProgramType } from "#/lib/carrot-api/types/Benefit"
import {
  BookKeyIcon,
  HandCoinsIcon,
  LightbulbIcon,
  MessageIcon,
  BanknoteIcon,
  SearchIcon
} from "@carrotfertility/carotene-core"

function useBenefitHighlights(): BenefitHighlightsResult {
  const { data: benefit, isLoading: isBenefitLoading } = useBenefit()
  const hasProviderFinder = benefit?.providerFinder.showProviderFinder
  const programType = benefit?.program.type
  const programTypeAllowsBenefitHighlight = programType === ProgramType.PRO || programType === ProgramType.CORE
  const filteredValueProps = getValuePropositions({ programType, hasProviderFinder })
  return { filteredValueProps, isBenefitLoading, programTypeAllowsBenefitHighlight, programType }
}

enum ValuePropositionTypes {
  PERSONALIZED_ADVICE = "PERSONALIZED_ADVICE",
  UNLIMITED_CHATS = "UNLIMITED_CHATS",
  EDUCATIONAL_RESOURCES = "EDUCATIONAL_RESOURCES",
  FUNDS = "FUNDS",
  PROVIDER_FINDER = "PROVIDER_FINDER",
  CARROT_COLLECTIVE = "CARROT_COLLECTIVE"
}

interface ValueProposition {
  valuePropId: ValuePropositionTypes
  valuePropMessage: MessageDescriptor
  icon: any
  link: string
}

interface BenefitHighlightsResult {
  filteredValueProps: ValueProposition[]
  isBenefitLoading: boolean
  programTypeAllowsBenefitHighlight: boolean
  programType: ProgramType
}

function getValuePropositions({
  programType,
  hasProviderFinder
}: {
  programType: ProgramType
  hasProviderFinder: boolean
}): ValueProposition[] {
  let exclusions: ValuePropositionTypes[] = []

  switch (programType) {
    case ProgramType.PRO:
      exclusions = [ValuePropositionTypes.PROVIDER_FINDER]
      break
    case ProgramType.CORE:
      exclusions = [ValuePropositionTypes.FUNDS]
      break
  }

  if (!hasProviderFinder) {
    exclusions.push(ValuePropositionTypes.PROVIDER_FINDER)
  }

  return valuePropositionOptions.filter((valueProp) => !exclusions.includes(valueProp.valuePropId))
}

const valuePropositionOptions: ValueProposition[] = [
  {
    valuePropId: ValuePropositionTypes.PERSONALIZED_ADVICE,
    valuePropMessage: defineMessage({ defaultMessage: "Personalized advice from benefit experts" }),
    icon: BookKeyIcon,
    link: TtcPaths.TTC_CARE_NAV
  },
  {
    valuePropId: ValuePropositionTypes.UNLIMITED_CHATS,
    valuePropMessage: defineMessage({ defaultMessage: "Unlimited chats with specialists in your journey" }),
    icon: MessageIcon,
    link: TtcPaths.TTC_LOCATION
  },
  {
    valuePropId: ValuePropositionTypes.EDUCATIONAL_RESOURCES,
    valuePropMessage: defineMessage({ defaultMessage: "Expert-produced educational resources" }),
    icon: LightbulbIcon,
    link: Paths.READ
  },
  {
    valuePropId: ValuePropositionTypes.FUNDS,
    valuePropMessage: defineMessage({ defaultMessage: "Funds for eligible expenses" }),
    icon: HandCoinsIcon,
    link: DbgPaths.CARROT_ELIGIBLE
  },
  {
    valuePropId: ValuePropositionTypes.PROVIDER_FINDER,
    valuePropMessage: defineMessage({ defaultMessage: "Access to a database of providers" }),
    icon: SearchIcon,
    link: Paths.PROVIDER_FINDER
  },
  {
    valuePropId: ValuePropositionTypes.CARROT_COLLECTIVE,
    valuePropMessage: defineMessage({ defaultMessage: "Exclusive discounts on products that support your journey" }),
    icon: BanknoteIcon,
    link: Paths.EXCLUSIVE_DISCOUNTS
  }
]

export type { ValueProposition }
export { useBenefitHighlights }
